/**
 * AgingReport - Accounts Receivable Aging Report
 * Shows outstanding invoices grouped by customer and age buckets
 */

import React, { useState, useEffect } from 'react';
import { AgingSummaryData, AgingSummaryRow } from '../../../types/accounting';
import { accountingApi } from '../../../services/accountingApi';

interface AgingReportProps {
  businessId: number;
}

const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD' }).format(amount);
};

const BUCKET_COLORS = {
  current: 'inherit',
  days_1_30: '#dc2626',
  days_31_60: '#c81e1e',
  days_61_90: '#b91c1c',
  days_90_plus: '#991b1b',
};

const AgingReport: React.FC<AgingReportProps> = ({ businessId }) => {
  const [data, setData] = useState<AgingSummaryData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchAging = async () => {
      setLoading(true);
      setError('');
      try {
        const response = await accountingApi.getAgingSummary(businessId);
        if (response.status === 'success' && response.data) {
          setData(response.data);
        } else {
          setError(response.message || 'Failed to load aging report');
        }
      } catch (err) {
        setError('Failed to load aging report');
      } finally {
        setLoading(false);
      }
    };
    fetchAging();
  }, [businessId]);

  if (loading) {
    return <div style={{ padding: '20px', textAlign: 'center' }}>Loading aging report...</div>;
  }

  if (error) {
    return <div style={{ padding: '20px', color: '#dc2626' }}>{error}</div>;
  }

  if (!data || data.customers.length === 0) {
    return (
      <div style={{ padding: '20px', textAlign: 'center', color: '#6b7280' }}>
        No outstanding invoices.
      </div>
    );
  }

  const renderAmount = (amount: number, color: string) => (
    <span style={{ color: amount > 0 ? color : 'inherit' }}>
      {formatCurrency(amount)}
    </span>
  );

  return (
    <div style={{ padding: '16px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <h3 style={{ margin: 0 }}>Accounts Receivable Aging</h3>
        <div style={{ fontSize: '14px', color: '#6b7280' }}>
          As of {data.as_of_date} | {data.customer_count} customer{data.customer_count !== 1 ? 's' : ''}
        </div>
      </div>

      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
        <thead>
          <tr style={{ borderBottom: '2px solid #e5e7eb', textAlign: 'right' }}>
            <th style={{ textAlign: 'left', padding: '8px' }}>Customer</th>
            <th style={{ padding: '8px' }}>Current</th>
            <th style={{ padding: '8px' }}>1-30 Days</th>
            <th style={{ padding: '8px' }}>31-60 Days</th>
            <th style={{ padding: '8px' }}>61-90 Days</th>
            <th style={{ padding: '8px' }}>90+ Days</th>
            <th style={{ padding: '8px' }}>Total</th>
          </tr>
        </thead>
        <tbody>
          {data.customers.map((row: AgingSummaryRow) => (
            <tr key={row.contact_id} style={{ borderBottom: '1px solid #f3f4f6' }}>
              <td style={{ padding: '8px', textAlign: 'left' }}>
                {row.contact_name}
                <span style={{ fontSize: '12px', color: '#9ca3af', marginLeft: '6px' }}>
                  ({row.invoice_count} inv.)
                </span>
              </td>
              <td style={{ padding: '8px', textAlign: 'right' }}>{renderAmount(row.current, BUCKET_COLORS.current)}</td>
              <td style={{ padding: '8px', textAlign: 'right' }}>{renderAmount(row.days_1_30, BUCKET_COLORS.days_1_30)}</td>
              <td style={{ padding: '8px', textAlign: 'right' }}>{renderAmount(row.days_31_60, BUCKET_COLORS.days_31_60)}</td>
              <td style={{ padding: '8px', textAlign: 'right' }}>{renderAmount(row.days_61_90, BUCKET_COLORS.days_61_90)}</td>
              <td style={{ padding: '8px', textAlign: 'right' }}>{renderAmount(row.days_90_plus, BUCKET_COLORS.days_90_plus)}</td>
              <td style={{ padding: '8px', textAlign: 'right', fontWeight: 600 }}>{formatCurrency(row.total)}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr style={{ borderTop: '2px solid #e5e7eb', fontWeight: 700 }}>
            <td style={{ padding: '8px', textAlign: 'left' }}>Total</td>
            <td style={{ padding: '8px', textAlign: 'right' }}>{renderAmount(data.totals.current, BUCKET_COLORS.current)}</td>
            <td style={{ padding: '8px', textAlign: 'right' }}>{renderAmount(data.totals.days_1_30, BUCKET_COLORS.days_1_30)}</td>
            <td style={{ padding: '8px', textAlign: 'right' }}>{renderAmount(data.totals.days_31_60, BUCKET_COLORS.days_31_60)}</td>
            <td style={{ padding: '8px', textAlign: 'right' }}>{renderAmount(data.totals.days_61_90, BUCKET_COLORS.days_61_90)}</td>
            <td style={{ padding: '8px', textAlign: 'right' }}>{renderAmount(data.totals.days_90_plus, BUCKET_COLORS.days_90_plus)}</td>
            <td style={{ padding: '8px', textAlign: 'right' }}>{formatCurrency(data.totals.total)}</td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
};

export default AgingReport;
